import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  ContactListResponseSchema,
  ContactSchema,
  type Contact,
  type ContactKind,
  type ContactWarm,
} from "@/api/types/contact";

interface UseContactsParams {
  kind?: ContactKind;
  warm?: ContactWarm;
  q?: string;
  limit?: number;
}

/** List query with cursor pagination — "load more" appends pages. */
export function useContacts(params: UseContactsParams = {}) {
  const { kind, warm, q, limit = 25 } = params;

  return useInfiniteQuery({
    queryKey: ["contacts", { kind, warm, q, limit }],
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (kind) searchParams.set("kind", kind);
      if (warm) searchParams.set("warm", warm);
      if (q?.trim()) searchParams.set("q", q.trim());
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(
        `/v2/contacts?${searchParams.toString()}`,
      );
      return validateResponse(
        ContactListResponseSchema,
        data,
        "GET /v2/contacts",
      );
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 60 * 1000,
  });
}

export function useContact(contactId: string | undefined) {
  return useQuery<Contact>({
    queryKey: ["contacts", contactId],
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/contacts/${contactId}`);
      return validateResponse(ContactSchema, data, "GET /v2/contacts/:id");
    },
    enabled: !!contactId,
  });
}

export interface ContactCreateInput {
  kind: ContactKind;
  name: string;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  instagram?: string | null;
  source?: string | null;
  warm: ContactWarm;
  tags: string[];
  notes?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  email_consent?: boolean;
  sms_consent?: boolean;
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  return useMutation<Contact, unknown, ContactCreateInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/contacts", input);
      return validateResponse(ContactSchema, data, "POST /v2/contacts");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  return useMutation<
    Contact,
    unknown,
    { id: string } & Partial<ContactCreateInput>
  >({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/contacts/${id}`, patch);
      return validateResponse(ContactSchema, data, "PATCH /v2/contacts/:id");
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(["contacts", updated.id], updated);
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/v2/contacts/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
  });
}
